<template>
  <!--
    The compact variant is for the auth pages.

    AuthShell's form area is `flex: 1`, so it claims the viewport minus the
    header and the full footer then stacks below it — the page was structurally
    guaranteed to be a footer taller than the window. A sign-in screen has no
    use for a brand block and nine social links anyway; the legal links stay,
    because Google's OAuth consent review expects a reachable privacy policy
    from the screen that offers "Sign in with Google".
  -->
  <!--
    The strip variant is for full-viewport surfaces like the world map: one row,
    shorter than the header, so the page under it keeps the whole screen.
  -->
  <footer v-if="strip" class="footer footer--strip">
    <div class="footer__row">
      <router-link to="/" class="footer__row-mark" :aria-label="t('navigation.brandHome')">
        <BrandMark />
        <span>© {{ currentYear }} Dimension&#8209;X</span>
      </router-link>
      <nav class="footer__row-social" :aria-label="t('footer.socialLabel')">
        <a
          v-for="social in socialLinks"
          :key="social.name"
          :href="social.url"
          class="footer__row-icon"
          target="_blank"
          rel="noopener noreferrer"
        >
          <i :class="social.icon" aria-hidden="true"></i>
          <span class="visually-hidden">{{ social.name }}</span>
        </a>
      </nav>
      <nav class="footer__row-legal" :aria-label="t('footer.legalLabel')">
        <router-link :to="{ name: 'PrivacyPolicy' }">{{ t('footer.privacy') }}</router-link>
        <router-link :to="{ name: 'AccountDeletion' }">{{ t('footer.accountDeletion') }}</router-link>
      </nav>
    </div>
  </footer>

  <footer v-else-if="compact" class="footer footer--compact">
    <div class="footer__strip">
      <p class="footer__copy">© {{ currentYear }} Dimension-X</p>
      <nav class="footer__legal" :aria-label="t('footer.legalLabel')">
        <router-link :to="{ name: 'PrivacyPolicy' }">{{ t('footer.privacy') }}</router-link>
        <router-link :to="{ name: 'AccountDeletion' }">{{ t('footer.accountDeletion') }}</router-link>
      </nav>
    </div>
  </footer>

  <footer v-else class="footer">
    <div class="footer__inner">
      <div class="footer__brand">
        <router-link to="/" class="footer__mark" :aria-label="t('navigation.brandHome')">
          <BrandMark />
          <span>Dimension&#8209;X</span>
        </router-link>
        <p class="footer__copy">© {{ currentYear }} Dimension-X. {{ t('footer.allRightsReserved') }}</p>
        <p class="footer__copy">
          {{ t('footer.createdBy') }}
          <a href="mailto:moaddib666@gmail.com">{{ t('footer.creator') }}</a>
        </p>
      </div>

      <nav class="footer__social" :aria-label="t('footer.socialLabel')">
        <a
          v-for="social in socialLinks"
          :key="social.name"
          :href="social.url"
          class="footer__social-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          <i :class="social.icon" aria-hidden="true"></i>
          <span class="visually-hidden">{{ social.name }}</span>
        </a>
      </nav>

      <nav class="footer__legal" :aria-label="t('footer.legalLabel')">
        <router-link :to="{ name: 'PrivacyPolicy' }">{{ t('footer.privacy') }}</router-link>
        <router-link :to="{ name: 'AccountDeletion' }">{{ t('footer.accountDeletion') }}</router-link>
      </nav>
    </div>
  </footer>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import { socialLinks } from '@/data/socialLinks';
import BrandMark from './BrandMark.vue';

export default {
  name: 'Footer',
  components: { BrandMark },
  props: {
    compact: Boolean,
    strip: Boolean,
  },
  setup() {
    const { t } = useI18n();
    const currentYear = computed(() => new Date().getFullYear());
    return { t, currentYear, socialLinks };
  }
}
</script>

<style scoped>
/*
 * Rebuilt onto tokens.css, and demoted.
 *
 * The footer was nine 60px keycaps carrying the full --shadow-raised stack —
 * the same treatment as the landing's primary button, applied nine times in a
 * row, each with its own brand-coloured hover. It was the heaviest object on
 * every page and it sat at the end of the funnel, competing with the CTA
 * directly above it. Social links are a courtesy, not an action; they are
 * hairline icon buttons now, and the labels live in the accessible name.
 */
.footer {
  margin-top: auto;
  padding: var(--space-8) 0 var(--space-7);
  border-top: 1px solid var(--border);
  background: rgba(6, 9, 13, 0.6);
}

/* ---- Compact (auth pages) -------------------------------------------- */
.footer--compact {
  padding: var(--space-4) 0;
}

.footer__strip {
  max-width: var(--page-max-wide);
  margin: 0 auto;
  padding: 0 var(--page-gutter);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: var(--space-2) var(--space-5);
  font-size: var(--text-xs);
}

.footer__inner {
  max-width: var(--page-max-wide);
  margin: 0 auto;
  padding: 0 var(--page-gutter);
  display: grid;
  gap: var(--space-6);
  align-items: center;
  grid-template-areas:
    'brand'
    'social'
    'legal';
  justify-items: center;
  text-align: center;
}

.footer__brand {
  grid-area: brand;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.footer__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-3);
  color: var(--text);
  font-family: var(--font-display);
  font-size: 0.9rem;
  letter-spacing: 0.08em;
  text-decoration: none;
  margin-bottom: var(--space-1);
}

.footer__mark :deep(svg) {
  width: 22px;
  height: 22px;
  color: var(--accent-dim);
}

.footer__copy {
  margin: 0;
  font-size: var(--text-xs);
  color: var(--text-faint);
}

.footer__copy a {
  color: var(--text-muted);
}

.footer__copy a:hover {
  color: var(--accent);
}

.footer__social {
  grid-area: social;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-2);
}

.footer__social-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  color: var(--text-faint);
  font-size: 0.95rem;
  transition: color var(--duration) var(--ease),
              border-color var(--duration) var(--ease);
}

/* One hover colour, not nine. The per-platform brand colours turned a hover
 * over the row into a rainbow and had nothing to do with this site. */
.footer__social-link:hover {
  color: var(--text);
  border-color: var(--border-strong);
  text-decoration: none;
}

.footer__legal {
  grid-area: legal;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-2) var(--space-5);
  font-size: var(--text-xs);
}

.footer__legal a {
  color: var(--text-faint);
  text-decoration: none;
}

.footer__legal a:hover {
  color: var(--text-muted);
  text-decoration: underline;
}

/* ---- Strip (full-viewport pages) ------------------------------------- */
.footer--strip {
  flex: none;
  height: var(--footer-strip-h);
  padding: 0;
  background: rgba(4, 8, 11, 0.94);
}

.footer__row {
  display: flex;
  align-items: center;
  gap: var(--space-5);
  height: 100%;
  /* Clear of the signed-in feedback button, which owns the bottom-right corner. */
  padding: 0 4.5rem 0 var(--space-4);
  font-size: var(--text-xs);
  white-space: nowrap;
}

.footer__row-mark {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--text-faint);
  text-decoration: none;
}

.footer__row-mark :deep(svg) {
  width: 14px;
  height: 14px;
}

.footer__row-mark:hover {
  color: var(--text-muted);
}

.footer__row-social {
  display: flex;
  gap: var(--space-1);
  margin-left: auto;
}

.footer__row-icon {
  display: inline-grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border-radius: var(--radius-sm);
  color: var(--text-faint);
  font-size: 0.75rem;
  transition: color var(--duration) var(--ease);
}

.footer__row-icon:hover,
.footer__row-icon:focus-visible {
  color: var(--text);
}

.footer__row-legal {
  display: flex;
  gap: var(--space-4);
}

.footer__row-legal a {
  color: var(--text-faint);
  text-decoration: none;
}

.footer__row-legal a:hover {
  color: var(--text-muted);
  text-decoration: underline;
}

@media (max-width: 40rem) {
  .footer__row-social {
    display: none;
  }

  .footer__row-legal {
    margin-left: auto;
    gap: var(--space-3);
  }
}

@media (min-width: 48rem) {
  .footer__inner {
    grid-template-areas: 'brand social legal';
    grid-template-columns: 1fr auto 1fr;
    justify-items: stretch;
    text-align: left;
    gap: var(--space-7);
  }

  .footer__brand {
    align-items: flex-start;
  }

  .footer__legal {
    justify-content: flex-end;
  }
}
</style>
